// @flow
import moment from 'moment'
import * as React from 'react'
import type { Question } from 'types/question'
import type { Response } from 'types/response'
import type { SessionObject } from 'types/session-object'
import { QuestionTitle } from 'ui/Text'
import { hasText } from 'utils/helpers'

import { TriggerUserActions } from '@/components/TriggerUserActions'
import { IndependentDatetimePicker } from '@/ui/molecules/IndependentDatetimePicker'

import ZentakeFormContext from '../ZentakeFormContext'

type Props = {
  question: Question,
  isOfficeUse: boolean,
  response: Response,
  sessions?: SessionObject,
  mode?: string,
}

function DateQuestion({
  question,
  isOfficeUse,
  response,
  sessions,
  mode,
}: Props): React.Node {
  const formContext = React.useContext(ZentakeFormContext)
  const readonly = formContext.isQuestionReadonly(question)
  const willShowNotAllowedCursor =
    formContext?.willShowNotAllowedCursor?.(question)

  const answer = formContext?.getAnswer?.(question)
  const value = React.useMemo(() => {
    return answer?.answer_raw
      ? answer?.answer_raw
      : question?.default_value || ''
  }, [answer?.answer_raw, question?.default_value])

  const isDirty = formContext?.isQuestionDirty?.(question)
  const isValid = formContext?.isQuestionValid?.(question)
  const questionError = formContext?.getQuestionError?.(question)

  const questionSubtype =
    typeof question?.question_type === 'object'
      ? question?.question_type?.question_subtype
      : ''

  const isMonthYear = questionSubtype === 'month_year'
  const isDateTime = questionSubtype === 'datetime'

  const pickerType = React.useMemo(() => {
    if (isMonthYear) {
      return 'month'
    }
    return null
  }, [isMonthYear])

  const [hasError, setHasError] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState(
    'This field is required'
  )

  const officeUsePreview = mode === 'preview' && question.is_office_use_only

  const formatValue = React.useMemo(() => {
    if (isMonthYear) {
      return 'MM-YYYY'
    }
    if (isDateTime) {
      return 'YYYY-MM-DDTHH:mm:ss'
    }
    return 'YYYY-MM-DD'
  }, [isMonthYear, isDateTime])

  const formatCurrentDate = React.useCallback(
    (date: ?Date) => {
      if (!date) return undefined

      return moment(date).format(formatValue)
    },
    [formatValue]
  )

  React.useEffect(() => {
    if (
      question?.date_default_value_now &&
      !answer?.answer_raw &&
      formContext?.onAnswerChanged
    ) {
      formContext?.onAnswerChanged?.(
        question,
        formatCurrentDate(moment().toDate())
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    setHasError(
      (isDirty && question?.is_required && !isValid) || hasText(questionError)
    )
    setErrorMessage(
      questionError && typeof questionError === 'string'
        ? questionError
        : 'This field is required'
    )
  }, [isDirty, question.is_required, isValid, questionError])

  function handleChange(nextValue: ?string) {
    setHasError(false)
    setErrorMessage('This field is required')

    formContext.onAnswerChanged?.(question, nextValue)
  }

  return (
    <div>
      <QuestionTitle
        isOfficeUse={isOfficeUse || officeUsePreview}
        isRequired={question.is_required}
      >
        {question.text}
      </QuestionTitle>
      <TriggerUserActions>
        <IndependentDatetimePicker
          data-form-id={response?.form?.id}
          data-packet-id={
            sessions?.fillResponseCustomer?.packetResponse?.packet?.id
          }
          data-response-id={response?.id}
          errorLabel={errorMessage}
          hasError={hasError}
          id={`q-${question.id}`}
          isOfficeUse={isOfficeUse || officeUsePreview}
          isRequired={question?.is_required}
          key={value}
          maxDate={question?.date_max}
          minDate={question?.date_min}
          onChange={(_value) => handleChange(_value ? _value : undefined)}
          picker={pickerType}
          readonly={readonly}
          showFutureOnly={question?.date_future_only}
          showPastOnly={question?.date_past_only}
          showTime={isDateTime}
          type="date"
          value={value}
          willShowNotAllowedCursor={willShowNotAllowedCursor}
        />
      </TriggerUserActions>
    </div>
  )
}

export default DateQuestion
